import { supabase } from '@/lib/supabase/client';
import type { CashflowEvent, CashflowEventInsert, CashflowEventUpdate } from '@/types/cashflow-events';

/**
 * Structured logging helper for service layer.
 * 
 * Gates logging by environment - only logs in development mode.
 * This ensures production builds don't include debug logging overhead.
 * 
 * Logging strategy:
 * - Development: Full structured logging with error details and metadata
 * - Production: Silent (no logging to avoid performance impact)
 * 
 * Use this instead of console.error for consistent, structured logging.
 * 
 * @param context - Context description of where the error occurred (e.g., "Error fetching cashflow events")
 * @param error - The error object (Error instance or unknown)
 * @param metadata - Optional metadata object for additional context (e.g., { eventId: "123", data: {...} })
 * 
 * @example
 * logServiceError('Error fetching cashflow events', error);
 * 
 * @example
 * logServiceError('Error updating cashflow event', error, { eventId: id, data: updateData });
 */
function logServiceError(context: string, error: unknown, metadata?: Record<string, unknown>) {
  if (import.meta.env.DEV) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;
    console.error(`[CashflowEventsService] ${context}`, {
      error: errorMessage,
      stack: errorStack,
      ...metadata,
    });
  }
}

// Helper to get current user ID
async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

export const cashflowEventsService = {
  async getAll(): Promise<CashflowEvent[]> {
    try {
      const { data, error } = await supabase.from('cashflow_events').select('*').order('date', { ascending: true });

      if (error) {
        logServiceError('Error fetching cashflow events', error);
        return [];
      }

      // Supabase returns data matching our CashflowEvent interface
      // Type assertion is safe here as schema matches TypeScript types
      return (data as CashflowEvent[]) ?? [];
    } catch (error) {
      logServiceError('Error fetching cashflow events', error);
      return [];
    }
  },

  async getById(id: string): Promise<CashflowEvent | null> {
    try {
      const { data, error } = await supabase.from('cashflow_events').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        logServiceError('Error fetching cashflow event', error, { eventId: id });
        return null;
      }

      // Supabase returns data matching our CashflowEvent interface
      // Type assertion is safe here as schema matches TypeScript types
      return (data as CashflowEvent) ?? null;
    } catch (error) {
      logServiceError('Error fetching cashflow event', error, { eventId: id });
      return null;
    }
  },

  async getByDateRange(startDate: string, endDate: string): Promise<CashflowEvent[]> {
    try {
      const { data, error } = await supabase
        .from('cashflow_events')
        .select('*')
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: true });

      if (error) {
        logServiceError('Error fetching cashflow events by date range', error, { startDate, endDate });
        return [];
      }

      return (data as CashflowEvent[]) ?? [];
    } catch (error) {
      logServiceError('Error fetching cashflow events by date range', error, { startDate, endDate });
      return [];
    }
  },

  async getByDealId(dealId: string): Promise<CashflowEvent[]> {
    try {
      const { data, error } = await supabase
        .from('cashflow_events')
        .select('*')
        .eq('deal_id', dealId)
        .order('date', { ascending: true });

      if (error) {
        logServiceError('Error fetching cashflow events by deal ID', error, { dealId });
        return [];
      }

      return (data as CashflowEvent[]) ?? [];
    } catch (error) {
      logServiceError('Error fetching cashflow events by deal ID', error, { dealId });
      return [];
    }
  },

  async create(data: CashflowEventInsert): Promise<CashflowEvent> {
    try {
      const userId = await getCurrentUserId();

      const insertData: Record<string, unknown> = {
        deal_id: data.deal_id ?? null,
        cogs_event_id: data.cogs_event_id ?? null,
        source_type: data.source_type,
        date: data.date,
        direction: data.direction,
        amount: data.amount,
        is_projection: data.is_projection ?? true,
        system_prefix: data.system_prefix ?? null,
        user_label: data.user_label ?? null,
        description: data.description ?? null,
        sort_order: data.sort_order ?? null,
        created_by: userId,
      };
      
      // Only include is_active if it's provided (migration may not be run yet)
      if (data.is_active !== undefined) {
        insertData.is_active = data.is_active;
      }

      const { data: newEvent, error } = await supabase.from('cashflow_events').insert(insertData).select().single();

      if (error) {
        logServiceError('Error creating cashflow event', error, { data: insertData });
        throw new Error(`Failed to create cashflow event: ${error.message}`);
      }

      // Supabase returns data matching our CashflowEvent interface
      // Type assertion is safe here as schema matches TypeScript types
      return newEvent as CashflowEvent;
    } catch (error) {
      logServiceError('Error creating cashflow event', error, { data });
      throw error;
    }
  },

  async update(id: string, data: CashflowEventUpdate, expectedUpdatedAt?: string): Promise<CashflowEvent> {
    try {
      // Conflict detection: if expectedUpdatedAt is provided, check if event was modified
      if (expectedUpdatedAt) {
        const { data: currentEvent, error: fetchError } = await supabase
          .from('cashflow_events')
          .select('updated_at')
          .eq('id', id)
          .single();

        if (fetchError) {
          logServiceError('Error fetching cashflow event for conflict check', fetchError, { eventId: id });
          throw new Error(`Failed to fetch cashflow event: ${fetchError.message}`);
        }

        if (!currentEvent) {
          throw new Error('Cashflow event not found');
        }

        // Validate updated_at exists
        if (!currentEvent.updated_at) {
          throw new Error('Cashflow event missing updated_at timestamp');
        }

        // Compare updated_at timestamps (convert to Date for comparison to handle timezone differences)
        const currentUpdatedAt = new Date(currentEvent.updated_at).getTime();
        const expectedUpdatedAtTime = new Date(expectedUpdatedAt).getTime();
        
        // Allow 1 second tolerance for clock skew
        if (Math.abs(currentUpdatedAt - expectedUpdatedAtTime) > 1000) {
          throw new Error('Concurrent edit detected: This row was modified by another user. Please refresh the page and try again.');
        }
      }

      const updateData: Record<string, unknown> = {};

      // Only include fields that are provided
      if (data.deal_id !== undefined) updateData.deal_id = data.deal_id;
      if (data.cogs_event_id !== undefined) updateData.cogs_event_id = data.cogs_event_id;
      if (data.source_type !== undefined) updateData.source_type = data.source_type;
      if (data.date !== undefined) updateData.date = data.date;
      if (data.direction !== undefined) updateData.direction = data.direction;
      if (data.amount !== undefined) updateData.amount = data.amount;
      if (data.is_projection !== undefined) updateData.is_projection = data.is_projection;
      // Only include is_active if it's provided (migration may not be run yet)
      if (data.is_active !== undefined) {
        updateData.is_active = data.is_active;
      }
      if (data.system_prefix !== undefined) updateData.system_prefix = data.system_prefix;
      if (data.user_label !== undefined) updateData.user_label = data.user_label;
      if (data.description !== undefined) updateData.description = data.description;
      if (data.sort_order !== undefined) updateData.sort_order = data.sort_order;

      const { data: updatedEvent, error } = await supabase
        .from('cashflow_events')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        logServiceError('Error updating cashflow event', error, { eventId: id, data });
        throw new Error(`Failed to update cashflow event: ${error.message}`);
      }

      if (!updatedEvent) {
        throw new Error('Cashflow event not found');
      }

      return updatedEvent as CashflowEvent;
    } catch (error) {
      logServiceError('Error updating cashflow event', error, { eventId: id, data });
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('cashflow_events').delete().eq('id', id);

      if (error) {
        logServiceError('Error deleting cashflow event', error, { eventId: id });
        throw new Error(`Failed to delete cashflow event: ${error.message}`);
      }
    } catch (error) {
      logServiceError('Error deleting cashflow event', error, { eventId: id });
      throw error;
    }
  },

  async deleteBatch(ids: string[]): Promise<number> {
    try {
      if (ids.length === 0) {
        return 0;
      }

      const { data, error } = await supabase
        .from('cashflow_events')
        .delete()
        .in('id', ids)
        .select();

      if (error) {
        logServiceError('Error batch deleting cashflow events', error, { eventIds: ids, count: ids.length });
        throw new Error(`Failed to delete cashflow events: ${error.message}`);
      }

      return data?.length || 0;
    } catch (error) {
      logServiceError('Error batch deleting cashflow events', error, { eventIds: ids, count: ids.length });
      throw error;
    }
  },

  async markAsActual(id: string): Promise<CashflowEvent> {
    try {
      const { data: updatedEvent, error } = await supabase
        .from('cashflow_events')
        .update({ is_projection: false })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        logServiceError('Error marking cashflow event as actual', error, { eventId: id });
        throw new Error(`Failed to mark cashflow event as actual: ${error.message}`);
      }

      if (!updatedEvent) {
        throw new Error('Cashflow event not found');
      }

      return updatedEvent as CashflowEvent;
    } catch (error) {
      logServiceError('Error marking cashflow event as actual', error, { eventId: id });
      throw error;
    }
  },
};

